import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView } from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import ProgressBarComponent from "@/components/ProgressBarComponent";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import Input from "@/components/input/Input";
import InputDropList from "@/components/input/InputDropList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { sendRequest } from "@/utils/ApiUtil";
import { MailingAddressObject } from "@/utils/dto/application.request";

type FormData = {
  unitNumber: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  country: string;
  city: string;
  state: string;
  postalCode: string;
  postalCodeUs: string;
  countryCode: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

interface StateData {
  state: string;
  stateCode: string;
  countryCode: string;
}

interface CityData {
  city: string;
  stateCode: string;
  countryCode: string;
}

export default function Address() {
  const [searchTerm, setSearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [states, setStates] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [cities, setCities] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [stateAcronym, setStateAcronym] = useState("");
  const [countryAcronym, setCountryAcronym] = useState("");

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const globalValues = useAppSelector((state: any) => state);

  const resolver = classValidatorResolver(MailingAddressObject, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, setValue, formState, setFocus } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountries(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/states", {
          countryCode: countryAcronym,
          searchTerm: stateSearchTerm,
        });
        const mappedStates = response.map((state: StateData) => ({
          value: state.state,
          code: state.stateCode,
          uniqueKey: `state-${state.stateCode}-${state.state}`,
        }));
        setStates(mappedStates);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    if (stateSearchTerm) {
      fetchData();
    }
  }, [stateSearchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/cities", {
          countryCode: countryAcronym,
          stateCode: stateAcronym,
          searchTerm: citySearchTerm,
        });
        const mappedStates = response.map((city: CityData) => ({
          value: city.city,
          code: city.stateCode,
          uniqueKey: `city-${city.city}-${city.stateCode}`,
        }));
        setCities(mappedStates);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    if (citySearchTerm) {
      fetchData();
    }
  }, [citySearchTerm]);

  const onSubmit = async (data: FormData) => {
    const applicationId = storage.getString("user.auth.applicationId");
    const type = storage.getString("user.auth.applicationType");
    if (data.countryCode === "US" && data.state.length > 2) {
      Toast.show({
        type: "error",
        autoHide: false,
        props: {
          text1: t("common:toast.error"),
          text2: t("common:toast.subtitle.statesus"),
        },
      });
      return;
    }
    const mailingAddressFormData = {
      unitNumber: data.unitNumber,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      addressLine3: data.addressLine3,
      city: data.city,
      state: data.state,
      postalCode: data.postalCodeUs || data.postalCode,
      countryCode: data.countryCode,
    };

    const mailingAddress: { [key: string]: string } = {};

    for (const [key, value] of Object.entries(mailingAddressFormData)) {
      if (value !== null && value !== undefined && value !== "") {
        mailingAddress[key] = value;
      }
    }
    const mailingData = {
      applicationId,
      mailingAddress,
    };
    //If is an individual onboarding, send request to create mailing address.
    if (type === "INDIVIDUAL") {
      try {
        await sendRequest("post", "/mailing-address", mailingData, true);
        router.replace("/individual/termsandconditions");
      } catch (error: any) {
        console.log(error);
      }
    } else if (type === "CORPORATION") {
      try {
        //If is and corporation onboarding and is an review process.
        if (globalValues.isIndividualReviewing) {
          const individualId = globalValues.currentCorporationIndividualId;
          const individualData = {
            applicationId,
            individualId,
            update: {
              ...globalValues.individualCustomerData,
              mailingAddress,
            },
          };
          const updateIndividual = await sendRequest(
            "patch",
            "/update-individual",
            individualData,
            true,
          );
          if (updateIndividual) {
            router.replace("/corporation/addindividual");
            dispatch(setForm({ isIndividualReviewing: false }));
            dispatch(setForm({ isIndividualReviewFinished: true }));
          }
        }
        //If is and corporation onboarding and is adding and individual.
        else {
          const addIndividualData = {
            applicationId,
            customerDetails: {
              individualType: globalValues.individualType,
              ...globalValues.individualCustomerData,
              percentageOwnership: globalValues.percentageOwnership,
              relationshipEstablishedAt: globalValues.relationshipEstablishedAt,
              mailingAddress,
            },
          };
          const sendIndividual = await sendRequest(
            "post",
            "/add-individual",
            addIndividualData,
            true,
          );
          if (sendIndividual.other && Array.isArray(sendIndividual.other)) {
            dispatch(
              setForm({
                corporationIndividualId: sendIndividual.other[0]?.data?.id,
              }),
            );
          }
          router.replace(
            globalValues.individualType === "BENEFICIAL_OWNER" ||
              globalValues.individualType === "CONTROL_PERSON"
              ? "/documents"
              : "/corporation/addindividual",
          );
        }
      } catch (error: any) {
        console.log(error);
      }
    }
  };

  const handleCountry = (data: { value: string; code: string }) => {
    dispatch(setForm({ country: data.value }));
    dispatch(setForm({ countryAcronym: data.code }));
    setCountryAcronym(data.code);
    setValue("country", data.value, { shouldValidate: true });
    setValue("countryCode", data.code, { shouldValidate: true });
    setFocus("state");
    setCountries([]);
  };
  const handleState = (data: { value: string; code: string }) => {
    setStateAcronym(data.code);
    if (countryAcronym === "US") {
      setValue("state", data.code, { shouldValidate: true });
    } else {
      setValue("state", data.value, { shouldValidate: true });
    }
    setFocus("city");
    setStates([]);
  };
  const handleCity = (data: { value: string; code: string }) => {
    setValue("city", data.value, { shouldValidate: true });
    if (countryAcronym === "US") {
      setFocus("postalCodeUs");
    } else {
      setFocus("postalCode");
    }
    setCities([]);
  };

  return (
    <ScrollView contentContainerClassName="flex-grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <View className="items-center justify-center w-full h-full">
        <Logo className="m-2" />
        <View className="m-2">
          <Title text={t("onboarding/individual/address:title")} />
        </View>
        <View className="items-center h-fit w-fit lg:w-3/5 lg:h-152 lg:border lg:border-solid lg:border-gray lg:rounded-md lg:shadow">
          <View className="w-full items-center">
            <ProgressBarComponent progress={0.5} animatedValue={0.75} />
          </View>
          <View className="flex-grow justify-center w-full items-center flex-col lg:flex-row">
            <View className="mx-5">
              <View className="z-50">
                <InputDropList
                  label={t("onboarding/individual/address:input.country")}
                  handleChange={(data) => {
                    handleCountry(data);
                  }}
                  clearValueFunction={() => {
                    setCountries([]);
                  }}
                  control={control}
                  name="countryCode"
                  data={countries}
                  onSearchChange={(term) => setSearchTerm(term)}
                  placeholder={t("common:placeholder.country")}
                />
              </View>
              <View className="z-40">
                <InputDropList
                  label={t("onboarding/individual/address:input.state")}
                  handleChange={(data) => {
                    handleState(data);
                  }}
                  clearValueFunction={() => {
                    setStates([]);
                  }}
                  control={control}
                  name="state"
                  data={states}
                  onSearchChange={(term) => setStateSearchTerm(term)}
                  placeholder={t("common:placeholder.state")}
                />
              </View>
              <View className="z-30">
                <InputDropList
                  label={t("onboarding/individual/address:input.city")}
                  handleChange={(data) => {
                    handleCity(data);
                  }}
                  clearValueFunction={() => {
                    setCities([]);
                  }}
                  control={control}
                  name="city"
                  data={cities}
                  onSearchChange={(term) => setCitySearchTerm(term)}
                  placeholder={t("common:placeholder.city")}
                />
              </View>
              <Input
                label={t("onboarding/individual/address:input.postalCode")}
                control={control}
                name={
                  countryAcronym && countryAcronym === "US"
                    ? "postalCodeUs"
                    : "postalCode"
                }
                type="alpha"
              />
            </View>
            <View className="mx-5">
              <Input
                label={t("onboarding/individual/address:input.addressLine1")}
                control={control}
                name="addressLine1"
                type="alpha"
              />
              <Input
                label={t("onboarding/individual/address:input.addressLine2")}
                control={control}
                name="addressLine2"
                type="alpha"
                optional
              />
              <Input
                label={t("onboarding/individual/address:input.addressLine3")}
                control={control}
                name="addressLine3"
                type="alpha"
                optional
              />
              <Input
                label={t("onboarding/individual/address:input.unitNumber")}
                control={control}
                name="unitNumber"
                type="alpha"
                optional
              />
            </View>
          </View>
          <View className="w-full items-center">
            <Button
              label={t("common:button.nextstep")}
              onClick={handleSubmit(onSubmit)}
            />
          </View>
        </View>
        <View className="flex-grow justify-end">
          <Copyright />
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
